import { useEffect, useRef, useState } from "react";
import {
  MessageSquare,
  ListTodo,
  StickyNote,
  User,
  Link as LinkIcon,
  Mic,
  Square,
  Camera,
  Image as ImageIcon,
  Highlighter,
  Inbox,
  Upload,
  Wand,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  type LucideIcon,
} from "lucide-react";
import { api, parseImageMeta, type CaptureRow, type IngestionPreview } from "../lib/api";
import { isSpeechSupported, startListening } from "../lib/speech";
import { getCsrfToken } from "../lib/auth";
import { ImageCapture, type ImageCaptureSource } from "../components/ImageCapture";
import { WikiAutocompleteTextarea } from "../components/WikiAutocompleteTextarea";
import { TeachingBanner } from "../components/TeachingBanner";

const TYPES: { id: string; label: string; Icon: LucideIcon }[] = [
  { id: "thought", label: "Thought", Icon: MessageSquare },
  { id: "task", label: "Task", Icon: ListTodo },
  { id: "note", label: "Note", Icon: StickyNote },
  { id: "person", label: "Person", Icon: User },
  { id: "link", label: "Link", Icon: LinkIcon },
  { id: "voice", label: "Voice", Icon: Mic },
  { id: "photo", label: "Photo", Icon: Camera },
  { id: "screenshot", label: "Screenshot", Icon: ImageIcon },
  { id: "highlight", label: "Highlight", Icon: Highlighter },
];

export function Capture() {
  const [text, setText] = useState("");
  const [type, setType] = useState("thought");
  const [recent, setRecent] = useState<CaptureRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const speechSupported = isSpeechSupported();
  const [listening, setListening] = useState(false);
  const [partial, setPartial] = useState("");
  const stopRef = useRef<(() => void) | null>(null);
  const baseTextRef = useRef<string>("");
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [intent, setIntent] = useState<IngestionPreview | null>(null);
  const [intentBusy, setIntentBusy] = useState(false);
  const [imageMode, setImageMode] = useState<ImageCaptureSource | null>(null);

  const loadRecent = () => {
    api
      .recentCaptures(10)
      .then((d) => setRecent(d.captures))
      .catch(() => {
        /* silent */
      });
  };

  useEffect(loadRecent, []);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(false), 1500);
    return () => clearTimeout(t);
  }, [success]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.capture(text.trim(), type, "private");
      setText("");
      setPartial("");
      setSuccess(true);
      loadRecent();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const startVoice = () => {
    if (listening) return;
    baseTextRef.current = text ? text + "\n" : "";
    setError(null);
    const handle = startListening({
      onPartial: (heard) => {
        setPartial(heard);
        setText((baseTextRef.current + heard).trim());
      },
      onError: (msg) => {
        setError(`voice: ${msg}`);
      },
    });
    if (!handle) {
      setError("Voice input is not supported in this browser. Try Android Chrome or a desktop browser.");
      return;
    }
    setListening(true);
    setType("voice");
    stopRef.current = handle.stop;
    handle.done.then(() => {
      setListening(false);
      setPartial("");
      stopRef.current = null;
    });
  };

  const stopVoice = () => {
    stopRef.current?.();
    setListening(false);
  };

  const previewIntent = async () => {
    if (!text.trim() || intentBusy) return;
    setIntentBusy(true);
    setIntent(null);
    try {
      const r = await api.ingestionPreview({ text: text.trim(), scopes: ["work"] });
      setIntent(r);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIntentBusy(false);
    }
  };

  const uploadFile = async (file: File) => {
    if (uploading) return;
    setUploading(true);
    setUploadMsg(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("scope", "private");
      const csrf = getCsrfToken();
      const res = await fetch("/api/documents", {
        method: "POST",
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
        body: form,
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${body.slice(0, 200)}`);
      }
      const body = (await res.json()) as { filename: string; extractedChars: number; captureId: string };
      setUploadMsg(`${body.filename} captured (${body.extractedChars.toLocaleString()} chars extracted).`);
      loadRecent();
    } catch (err) {
      setUploadMsg(`Upload failed: ${(err as Error).message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Capture
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Drop a thought, task, link, or anything. Type it or hold the mic.
        </p>
      </header>

      <TeachingBanner id="capture_quick_overview" />

      <form
        onSubmit={submit}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={async (e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) await uploadFile(file);
        }}
        className={`glass rounded-2xl p-4 sm:p-5 border ${
          dragOver
            ? "border-sky-500 ring-2 ring-sky-400/40"
            : "border-slate-200/70 dark:border-slate-700/50"
        } backdrop-blur space-y-3 transition-colors`}
      >
        <WikiAutocompleteTextarea
          value={text}
          onChange={setText}
          placeholder="What's on your mind?"
          rows={4}
          className="w-full resize-none bg-transparent text-base text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
        />

        {listening && (
          <div className="rounded-lg bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-700/50 px-3 py-2 text-xs text-rose-800 dark:text-rose-200 flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
            </span>
            Listening… {partial ? `"${partial.slice(-60)}"` : ""}
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          {TYPES.map((t) => {
            const active = type === t.id;
            const opensImage = t.id === "photo" || t.id === "screenshot";
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  if (opensImage) {
                    setImageMode(t.id as ImageCaptureSource);
                    return;
                  }
                  setType(t.id);
                }}
                aria-pressed={active}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  active
                    ? "bg-sky-500 text-white border-sky-500 shadow-sm"
                    : "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                <t.Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Saving to <span className="font-semibold">private</span> scope.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-slate-600 text-sm font-semibold px-3 py-2 transition-colors disabled:opacity-50"
              aria-label="Upload file"
            >
              <Upload className="w-4 h-4" />
              {uploading ? "Uploading…" : "File"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.pptx,.xlsx,.txt,.md,.csv"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadFile(f);
              }}
              className="hidden"
            />
            <button
              type="button"
              onClick={previewIntent}
              disabled={!text.trim() || intentBusy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-violet-200 dark:bg-violet-700/40 text-violet-900 dark:text-violet-100 hover:bg-violet-300 dark:hover:bg-violet-700/60 text-sm font-semibold px-3 py-2 transition-colors disabled:opacity-50"
              aria-label="Detect intent"
            >
              <Wand className="w-4 h-4" />
              {intentBusy ? "Detecting…" : "Detect"}
            </button>
            {speechSupported && (
              listening ? (
                <button
                  type="button"
                  onClick={stopVoice}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold px-3 py-2 transition-colors"
                >
                  <Square className="w-4 h-4" fill="currentColor" />
                  Stop
                </button>
              ) : (
                <button
                  type="button"
                  onClick={startVoice}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-slate-600 text-sm font-semibold px-3 py-2 transition-colors"
                  aria-label="Start voice input"
                >
                  <Mic className="w-4 h-4" />
                  Voice
                </button>
              )
            )}
            <button
              type="submit"
              disabled={!text.trim() || busy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 transition-colors"
            >
              {busy ? "Capturing…" : success ? "Captured ✓" : "Capture"}
            </button>
          </div>

          {uploadMsg && (
            <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">{uploadMsg}</div>
          )}
          {intent && (
            <div className="rounded-lg border border-violet-200 dark:border-violet-700/50 bg-violet-50 dark:bg-violet-900/30 px-3 py-2 text-xs text-violet-900 dark:text-violet-100 space-y-1">
              <div>
                <span className="font-bold uppercase tracking-wider mr-2">{intent.intent}</span>
                <span className="opacity-70">{intent.rationale}</span>
              </div>
              {intent.rewritten && intent.rewritten !== text && (
                <div className="text-slate-700 dark:text-slate-200">
                  <button
                    type="button"
                    onClick={() => {
                      setText(intent.rewritten);
                      setIntent(null);
                    }}
                    className="underline text-violet-700 dark:text-violet-300"
                  >
                    Use rewritten capture
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>
        )}
        {!speechSupported && (
          <div className="text-[11px] text-slate-400">
            Voice input isn't available in this browser. Android Chrome and desktop
            browsers work; iOS Safari is hit-or-miss.
          </div>
        )}
      </form>

      <section>
        <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-2">
          Recent
        </h2>
        {recent.length === 0 ? (
          <div className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-8 text-center space-y-2">
            <div className="inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-500 items-center justify-center text-white shadow-md">
              <Inbox className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Nothing captured yet
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              The first thought you drop in shows up here.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {recent.map((r) => (
              <RecentRow key={r.id} row={r} onChanged={loadRecent} />
            ))}
          </ul>
        )}
      </section>

      {imageMode && (
        <ImageCapture
          source={imageMode}
          scope="private"
          onClose={() => setImageMode(null)}
          onCaptured={() => {
            loadRecent();
          }}
        />
      )}
    </div>
  );
}

function RecentRow({ row, onChanged }: { row: CaptureRow; onChanged: () => void }) {
  const isImage = row.type === "photo" || row.type === "screenshot";
  const meta = isImage ? parseImageMeta(row.meta_json) : null;
  const [expanded, setExpanded] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessMsg, setReprocessMsg] = useState<string | null>(null);

  const ocrStatus = (row.ocr_status || null) as null | "pending" | "done" | "skipped" | "error";

  const reprocess = async () => {
    if (reprocessing) return;
    setReprocessing(true);
    setReprocessMsg(null);
    try {
      const res = await api.reprocessOcr(row.id);
      setReprocessMsg(
        res.ocr.status === "done"
          ? `OCR refreshed (${res.ocr.textChars.toLocaleString()} chars).`
          : res.ocr.degradation || `OCR ${res.ocr.status}.`,
      );
      onChanged();
    } catch (err) {
      setReprocessMsg((err as Error).message);
    } finally {
      setReprocessing(false);
    }
  };

  return (
    <li className="glass rounded-lg border border-slate-200/70 dark:border-slate-700/50 backdrop-blur">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left px-3 py-2.5 flex items-start gap-3"
        aria-expanded={expanded}
      >
        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 flex-shrink-0">
          {row.type}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-slate-900 dark:text-slate-100 break-words whitespace-pre-line line-clamp-3">
            {row.text}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{new Date(row.captured_at).toLocaleString()}</span>
            {isImage && ocrStatus && ocrStatus !== "done" && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                  ocrStatus === "skipped"
                    ? "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
                    : ocrStatus === "error"
                    ? "bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200"
                    : "bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200"
                }`}
              >
                {ocrStatus === "skipped" ? "OCR pending" : ocrStatus === "error" ? "OCR error" : "OCR running"}
              </span>
            )}
          </div>
        </div>
        {isImage && (
          <span className="text-slate-400 flex-shrink-0 mt-1">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </span>
        )}
      </button>

      {isImage && expanded && (
        <div className="border-t border-slate-200/70 dark:border-slate-700/50 px-3 py-3 space-y-3">
          <img
            src={`/api/captures/${encodeURIComponent(row.id)}/image`}
            alt={meta?.caption || "Captured image"}
            className="w-full max-h-96 object-contain rounded-lg bg-slate-100 dark:bg-slate-900"
          />
          {meta?.caption && (
            <div className="text-sm text-slate-800 dark:text-slate-200 italic">"{meta.caption}"</div>
          )}
          {meta?.ocrDegradation && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
              {meta.ocrDegradation}
            </div>
          )}
          {row.ocr_error && ocrStatus === "error" && (
            <div className="rounded-lg border border-rose-200 dark:border-rose-700/50 bg-rose-50 dark:bg-rose-900/30 px-3 py-2 text-xs text-rose-900 dark:text-rose-100">
              {row.ocr_error}
            </div>
          )}
          {ocrStatus !== "done" && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={reprocess}
                disabled={reprocessing}
                className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${reprocessing ? "animate-spin" : ""}`} />
                {reprocessing ? "Re-processing" : "Re-process OCR"}
              </button>
              {reprocessMsg && (
                <span className="text-[11px] text-slate-600 dark:text-slate-300">{reprocessMsg}</span>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}
